/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
// @flow

import { chunk, isEmpty } from 'lodash';
import { ISSUE_STATUS_CATEGORY_IDS_BY_KEY } from '../utils/consts';

interface PaginatedRequest {
    maxResultsPerPage?: number;
    maxPages?: number;
    maxParallelRequests?: number;
}

interface RequestConfig {
    baseUrl: string;
    params?: URLSearchParams;
    type?: string;
    bodyData?: Object | Array;
}

const requestAP = async ({
    baseUrl,
    params = new URLSearchParams(),
    type = 'GET',
    contentType = 'application/json',
    bodyData,
}: RequestConfig) => {
    let url = baseUrl;
    if (params.size) {
        url = `${url}?${params.toString()}`;
    }

    const typeIsGet = type.toLowerCase() === 'get';

    return window.PlatformAdapter.requestJira(url, {
        type,
        contentType: typeIsGet ? undefined : contentType,
        data: !typeIsGet && bodyData && JSON.stringify(bodyData),
    });
};

const fetchJiraList = async ({
    baseUrl,
    params = new URLSearchParams(),
    type = 'GET',
    contentType = 'application/json',
    bodyData,
    valuesProperty = 'values',
    maxPages,
    maxParallelRequests = 1,
}: RequestConfig & {
    valuesProperty?: string,
    maxPages?: number,
    maxParallelRequests?: number,
}) => {
    const resultList = [];

    const resultBody = await requestAP({ baseUrl, params, type, contentType, bodyData });
    resultList.push(...(resultBody[valuesProperty] ?? []));

    if (maxPages === 1) {
        return resultList;
    }

    const startAtList = [];
    let currentStartAt = resultBody.maxResults;
    do {
        if (resultBody.total > currentStartAt) {
            startAtList.push(currentStartAt);
        }
        currentStartAt += resultBody.maxResults;
    } while (resultBody.total > currentStartAt && (maxPages === undefined || startAtList.length + 1 < maxPages));

    const startAtChunks = chunk(startAtList, maxParallelRequests);
    for (const startAtChunk of startAtChunks) {
        const results = await Promise.all(
            startAtChunk.map(async (startAtValue) => {
                const chunkBodyData = bodyData ? { ...bodyData } : undefined;
                const chunkParams = new URLSearchParams(params);
                if (bodyData) {
                    chunkBodyData.startAt = startAtValue;
                } else {
                    chunkParams.set('startAt', startAtValue);
                }
                const chunkResultBody = await requestAP({
                    baseUrl,
                    params: chunkParams,
                    type,
                    contentType,
                    bodyData: chunkBodyData,
                });
                return chunkResultBody[valuesProperty] ?? [];
            }),
        );
        resultList.push(...results.flat());
    }

    return resultList;
};

const fetchJiraListUsingCursor = async ({
    baseUrl,
    params = new URLSearchParams(),
    type = 'GET',
    contentType = 'application/json',
    bodyData,
    valuesProperty = 'values',
    maxPages,
}: RequestConfig & {
    valuesProperty?: string,
    maxPages?: number,
}) => {
    let page = 0;
    const resultList = [];
    let nextPageToken: string | undefined;

    const reqParams = new URLSearchParams(params);
    const reqBodyData = bodyData ? { ...bodyData } : undefined;

    do {
        if (bodyData) {
            reqBodyData.nextPageToken = nextPageToken;
        } else {
            reqParams.set('nextPageToken', nextPageToken);
        }

        const resultBody = await requestAP({
            baseUrl,
            type,
            params: reqParams,
            contentType,
            bodyData: reqBodyData,
        });

        resultList.push(...(resultBody[valuesProperty] ?? []));

        page += 1;
        nextPageToken = resultBody.nextPageToken;
    } while (nextPageToken && (maxPages === undefined || page < maxPages));

    return resultList;
};

export const getJiraProject = async (jiraProjectKeyOrId) => {
    const baseUrl = `/rest/api/3/project/${jiraProjectKeyOrId}`;
    const project = await requestAP({
        baseUrl,
        type: 'GET',
    });
    return project;
};

export const getJiraProjects = async ({
    searchText,
    jiraProjectIds,
    orderBy = 'name',
    maxResultsPerPage,
    maxPages,
    maxParallelRequests,
}: PaginatedRequest & {
    searchText?: string,
    jiraProjectIds?: string[],
    orderBy?: string,
} = {}) => {
    const baseUrl = `/rest/api/3/project/search`;
    const params = new URLSearchParams({
        orderBy,
    });

    if (searchText) {
        params.set('query', searchText);
    }

    if (maxResultsPerPage) {
        params.set('maxResults', maxResultsPerPage);
    }

    if (!jiraProjectIds?.length) {
        return fetchJiraList({
            baseUrl,
            params,
            maxPages,
            maxParallelRequests,
        });
    }

    const jiraProjectIdsChunks = chunk(jiraProjectIds, 50);
    const chunksResp = await Promise.all(
        jiraProjectIdsChunks.map(async (jiraProjectIdsChunk) => {
            const chunkUrlParams = new URLSearchParams(params);
            jiraProjectIdsChunk.forEach((id) => chunkUrlParams.append('id', id));

            return fetchJiraList({
                baseUrl,
                params: chunkUrlParams,
                maxPages,
                maxParallelRequests,
            });
        }),
    );

    return chunksResp.flat();
};

type JiraProjectVersionStatus = 'released' | 'unreleased' | 'archived';

export const getJiraProjectVersions = async ({
    jiraProjectId,
    searchText,
    statuses,
    maxResultsPerPage,
    maxPages,
    maxParallelRequests,
}: PaginatedRequest & {
    jiraProjectId: string,
    searchText?: string,
    statuses?: JiraProjectVersionStatus[],
} = {}) => {
    const baseUrl = `/rest/api/3/project/${jiraProjectId}/version`;
    const params = new URLSearchParams({
        expand: 'issuesstatus',
        orderBy: 'name',
    });

    if (searchText) {
        params.set('query', searchText);
    }

    if (statuses?.length && isEmpty(searchText)) {
        statuses.forEach((status) => params.append('status', status));
    }

    if (maxResultsPerPage) {
        params.set('maxResults', maxResultsPerPage);
    }

    return fetchJiraList({
        baseUrl,
        params,
        maxPages,
        maxParallelRequests,
    });
};

export const getJiraIssues = async ({
    jql,
    maxResultsPerPage,
    maxPages,
    fields,
}: PaginatedRequest & {
    jql?: string,
    fields?: string[],
} = {}) => {
    const baseUrl = `/rest/api/3/search/jql`;
    const bodyData = {
        jql,
        fields,
        maxResults: maxResultsPerPage,
    };

    return fetchJiraListUsingCursor({
        baseUrl,
        type: 'POST',
        bodyData,
        valuesProperty: 'issues',
        maxPages,
    });
};

export const getJiraIssuesCountByJql = async ({ jql }: { jql: string }) => {
    const baseUrl = `/rest/api/3/search/approximate-count`;
    const bodyData = {
        jql,
    };

    const resultBody = await requestAP({
        baseUrl,
        type: 'POST',
        bodyData,
    });
    return resultBody?.count ?? 0;
};

export const getJiraIssuesCountGroupedByStatusCategoryId = async ({
    jql,
}: {
    jql: string,
}): Promise<Record<string, number>> => {
    const baseUrl = `/rest/api/3/expression/evaluate`;
    const expression = `issues.reduce((result, issue) => result.set(issue.status.category.key, (result[issue.status.category.key] || 0) + 1), new Map())`;

    const resultMap: Record<string, number> = {};
    let nextPageToken: string | undefined;
    do {
        const exprResult = await requestAP({
            baseUrl,
            type: 'POST',
            bodyData: {
                context: {
                    issues: {
                        jql: {
                            query: jql,
                            nextPageToken,
                        },
                    },
                },
                expression,
            },
        });

        for (const [key, count] of Object.entries(exprResult.value)) {
            const id = ISSUE_STATUS_CATEGORY_IDS_BY_KEY[key];
            if (id) {
                resultMap[id] = (resultMap[id] ?? 0) + count;
            }
        }

        nextPageToken = exprResult.meta.issues.jql.nextPageToken;
    } while (nextPageToken);

    return resultMap;
};

export const getJiraIssuesUrl = ({ baseUrl, jql }: { baseUrl: string, jql: string }) => {
    if (!baseUrl?.length) return null;
    const url = new URL(`${baseUrl}/issues/`);
    url.searchParams.append('jql', jql);
    return url.toString();
};

export const openIssuesUrl = (jql: string) => {
    const params = new URLSearchParams({ jql });

    window.PlatformAdapter.openNewTab(`/issues/?${params.toString()}`);
};

interface JiraIssuesRelatedToComponentJqlRequest {
    componentName: string;
    issuePropertyKey: string;
    projectIds?: string[];
    daysResolved?: string;
}

export const getJiraIssuesRelatedToComponentJql = ({
    componentName,
    projectsIds,
    daysResolved,
    issuePropertyKey,
}: JiraIssuesRelatedToComponentJqlRequest) => {
    const normalizedComponentName = componentName.replace(/[\\"]/g, '\\$&');

    const normalizedIssuePropertyKey =
        issuePropertyKey === 'Component' ? issuePropertyKey : `${issuePropertyKey}.value`;

    const jqlInstructions = [`"${normalizedIssuePropertyKey}" = "${normalizedComponentName}"`];

    if (projectsIds?.length) {
        jqlInstructions.push(`project in (${projectsIds.join(', ')})`);
    }

    if (daysResolved && daysResolved !== 'all') {
        jqlInstructions.push(`(statusCategory != Done OR resolved >= -${daysResolved}d)`);
    }

    return jqlInstructions.join(' AND ');
};

export const getJiraIssuesRelatedToComponent = async ({
    maxResultsPerPage,
    maxPages,
    ...rest
}: PaginatedRequest & JiraIssuesRelatedToComponentJqlRequest) => {
    const jql = getJiraIssuesRelatedToComponentJql(rest).concat(' ORDER BY resolved DESC');

    return getJiraIssues({
        jql,
        maxResultsPerPage,
        maxPages,
        fields: ['status', 'summary', 'key', 'issuetype', 'priority'],
    });
};
